/**
 * Градиенты воронки — решение владельца №2 (2026-10-08, повтор Slimkit):
 * «настройка градиента — угол + стопы там, где у эталона градиент; источник
 * значений — палитра/тема (роль „градиент бренда“) с переопределением на блоке».
 *
 * Запись — ОДНА строка CSS строго канонической формы:
 *   linear-gradient(<угол>deg, #hex[ <позиция>%], #hex[ <позиция>%][, …])
 * 2–5 стопов, цвета только кодом (#rgb / #rrggbb / #rrggbbaa), позиции —
 * проценты (у эталона бывают за 100: `#2777f2 160.52%`). Почему строка, а не
 * объект: её без перевода читает CSS (`background: <строка>`), а форма закрыта
 * так же, как код цвета в `color-fields.ts` сервера — внутри не может быть `;`,
 * кавычек, `url(` и прочего, что вырвалось бы за пределы свойства.
 *
 * ДВА КОНЦА: шаблон продублирован на сервере
 * (`backend/src/ai-funnels/block-schema/gradient-fields.ts`,
 * `FUNNEL_GRADIENT_PATTERN`). Совпадение текстов шаблонов держит сторож
 * `backend/src/ai-funnels/block-schema/gradient-fields.ends.spec.ts` — он
 * читает ЭТОТ файл с диска; не менять форму объявления, не обновив его.
 */

import { pickOnPrimaryColor } from './colorUtils';
import { opaqueContrastBaseOf } from './surfaceOpacity';

/** Канонический градиент воронки. Зеркало `FUNNEL_GRADIENT_PATTERN` сервера. */
export const FUNNEL_GRADIENT_PATTERN =
  /^linear-gradient\(-?\d{1,3}(?:\.\d{1,2})?deg(?:, #(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})(?: -?\d{1,4}(?:\.\d{1,2})?%)?){2,5}\)$/;

/** Потолок длины строки — 5 стопов по `#rrggbbaa -1000.00%` с запасом. */
export const FUNNEL_GRADIENT_MAX_LENGTH = 160;

export const GRADIENT_MIN_STOPS = 2;
export const GRADIENT_MAX_STOPS = 5;

export interface GradientStop {
  /** Код цвета (#rgb / #rrggbb / #rrggbbaa). */
  color: string;
  /** Позиция в процентах; отсутствие — браузер расставит стопы равномерно. */
  at?: number;
}

export interface ParsedGradient {
  /** Угол в градусах (CSS `deg`). */
  angle: number;
  stops: GradientStop[];
}

/**
 * Три роли «градиента бренда» в палитре/теме. Каждая — строка канонической
 * формы; отсутствие роли = сплошной цвет, как было до решения №2.
 *  - selected — заливка выбранного варианта ответа (режим пака `states.selected: 'gradient'`);
 *  - button   — заливка основной кнопки (`primaryButtonStyle`);
 *  - progress — заливка дорожки прогресса в шапке квиза.
 */
export interface FunnelGradients {
  selected?: string;
  button?: string;
  progress?: string;
}

export const FUNNEL_GRADIENT_ROLES = [
  'selected',
  'button',
  'progress',
] as const;
export type FunnelGradientRole = (typeof FUNNEL_GRADIENT_ROLES)[number];

/** CSS-переменная роли. Читатели: optionRowStyle/stylePackToStyle, primaryButtonStyle, шапка квиза. */
export const GRADIENT_CSS_VARS: Record<FunnelGradientRole, string> = {
  selected: '--gradient-selected',
  button: '--gradient-button',
  progress: '--gradient-progress',
};

/** Строка — канонический градиент воронки (форма и длина). */
export function isFunnelGradient(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length <= FUNNEL_GRADIENT_MAX_LENGTH &&
    FUNNEL_GRADIENT_PATTERN.test(value)
  );
}

/** Строка → угол и стопы; `null` для всего, что не каноническая форма. */
export function parseFunnelGradient(value: unknown): ParsedGradient | null {
  if (!isFunnelGradient(value)) return null;
  const inner = value.slice('linear-gradient('.length, -1);
  const [anglePart, ...stopParts] = inner.split(', ');
  const angle = Number(anglePart.slice(0, -'deg'.length));
  const stops = stopParts.map((part): GradientStop => {
    const [color, pos] = part.split(' ');
    return pos === undefined
      ? { color }
      : { color, at: Number(pos.slice(0, -1)) };
  });
  return { angle, stops };
}

function formatNumber(n: number): string {
  // Не больше двух знаков после точки — ровно столько пропускает шаблон.
  return String(Math.round(n * 100) / 100);
}

/**
 * Угол и стопы → каноническая строка. Возвращает `undefined`, если результат
 * не прошёл бы шаблон (негодный цвет, стопов меньше двух или больше пяти,
 * числа вне диапазона) — вызывающий тогда не пишет поле вовсе.
 */
export function formatFunnelGradient(
  gradient: ParsedGradient,
): string | undefined {
  if (!Number.isFinite(gradient.angle)) return undefined;
  const stops = gradient.stops.map((stop) =>
    typeof stop.at === 'number' && Number.isFinite(stop.at)
      ? `${stop.color} ${formatNumber(stop.at)}%`
      : stop.color,
  );
  const css = `linear-gradient(${formatNumber(gradient.angle)}deg, ${stops.join(', ')})`;
  return isFunnelGradient(css) ? css : undefined;
}

/**
 * Опорный цвет для авто-контраста текста поверх градиента — ПЕРВЫЙ стоп.
 * Тот же приём, что у кнопки темы 'gradient' (текст считается от
 * `--color-primary`, начала её градиента). Для негодной строки — `null`:
 * гадать о контрасте хуже, чем оставить прежний цвет (правило WEB-1674).
 */
export function gradientContrastBase(value: unknown): string | null {
  const parsed = parseFunnelGradient(value);
  return parsed ? parsed.stops[0].color : null;
}

/**
 * Роли градиентов → CSS-переменные корня воронки. Эмитятся ТОЛЬКО заданные и
 * годные роли: у темы/палитры без градиентов не появляется ни одного ключа —
 * старые воронки не меняются ни на байт.
 */
export function gradientsToStyle(
  gradients: FunnelGradients | undefined,
): Record<string, string> {
  const style: Record<string, string> = {};
  if (!gradients) return style;
  for (const role of FUNNEL_GRADIENT_ROLES) {
    const value = gradients[role];
    if (isFunnelGradient(value)) style[GRADIENT_CSS_VARS[role]] = value;
  }
  return style;
}

/**
 * WEB-3447, заход 2: градиент из поля автора (выбранный вариант, кнопка) —
 * обрезанная годная строка либо `undefined` («как будто поля нет»). Одно место
 * на оба поля: файл едет ленивым куском (`styleKit.ts`), первый экран зовёт
 * его через дверь `styleKitDoor.ts`.
 */
export function authorGradient(raw: string | undefined): string | undefined {
  const value = raw?.trim();
  return isFunnelGradient(value) ? value : undefined;
}

/**
 * Цвет текста поверх градиента автора — авто-контраст по первому стопу тем
 * же pickOnPrimaryColor, что у кнопки и строки варианта; полупрозрачный стоп
 * опорой не годится (порог WEB-1859). Нет опоры — `null`, цвет прежний.
 */
export function gradientTextColor(gradient: string | undefined): string | null {
  const base = opaqueContrastBaseOf(gradientContrastBase(gradient));
  return base ? pickOnPrimaryColor(base) : null;
}
